import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  alpha,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Fade,
  Zoom,
  Stack,
  Paper
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowForward,
  Close,
  EmojiEvents,
  CheckCircle,
  Speed,
  Security,
  Group,
  Celebration,
  RocketLaunch,
  AutoAwesome
} from '@mui/icons-material';
import confetti from 'canvas-confetti';
import { useTranslate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';

// Import step components
import { CompanySetupWalaxy } from './walaxy/CompanySetupWalaxy';
import { TeamInviteWalaxy } from './walaxy/TeamInviteWalaxy';
import { FeatureDemoWalaxy } from './walaxy/FeatureDemoWalaxy';
import { PaywallWalaxy } from './walaxy/PaywallWalaxy';
import { OnboardingDevTools } from './OnboardingDevTools';
import axiosInstance from "../../api/axios-config";


interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: any;
  unlocked: boolean;
}

export const B2BWalaxyStyle: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();

  // Check for dev mode configuration
  const isDev = process.env.NODE_ENV === 'development';
  const initialStep = isDev
    ? parseInt(sessionStorage.getItem('onboarding_start_step') || '0')
    : 0;
  const skipWelcome = isDev && initialStep > 0;

  const [activeStep, setActiveStep] = useState(initialStep);
  const [companyData, setCompanyData] = useState<any>(
    isDev ? { id: 999, nom: 'Dev Company', limitUser: 100 } : null
  );
  const [invitedUsers, setInvitedUsers] = useState<any[]>([]);
  const [showWelcome, setShowWelcome] = useState(!skipWelcome);
  const [achievements, setAchievements] = useState<Achievement[]>([
    {
      id: 'company-created',
      title: translate('onboarding.b2b.achievements.companyCreated.title'),
      description: translate('onboarding.b2b.achievements.companyCreated.description'),
      icon: RocketLaunch,
      unlocked: false
    },
    {
      id: 'team-invited',
      title: translate('onboarding.b2b.achievements.teamInvited.title'),
      description: translate('onboarding.b2b.achievements.teamInvited.description'),
      icon: Group,
      unlocked: false
    },
    {
      id: 'features-discovered',
      title: translate('onboarding.b2b.achievements.featuresDiscovered.title'),
      description: translate('onboarding.b2b.achievements.featuresDiscovered.description'),
      icon: AutoAwesome,
      unlocked: false
    },
    {
      id: 'trial-started',
      title: translate('onboarding.b2b.achievements.trialStarted.title'),
      description: translate('onboarding.b2b.achievements.trialStarted.description'),
      icon: Celebration,
      unlocked: false
    }
  ]);

  const steps = [
    {
      id: 'company',
      title: translate('onboarding.b2b.steps.company.title'),
      subtitle: translate('onboarding.b2b.steps.company.subtitle'),
      emoji: '🚀'
    },
    {
      id: 'team',
      title: translate('onboarding.b2b.steps.team.title'),
      subtitle: translate('onboarding.b2b.steps.team.subtitle'),
      emoji: '🎯'
    },
    {
      id: 'demo',
      title: translate('onboarding.b2b.steps.demo.title'),
      subtitle: translate('onboarding.b2b.steps.demo.subtitle'),
      emoji: '🪄'
    },
    {
      id: 'paywall',
      title: translate('onboarding.b2b.steps.paywall.title'),
      subtitle: translate('onboarding.b2b.steps.paywall.subtitle'),
      emoji: '💎'
    }
  ];

  const fireConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const unlockAchievement = (achievementId: string) => {
    setAchievements(prev =>
      prev.map(ach =>
        ach.id === achievementId ? { ...ach, unlocked: true } : ach
      )
    );
    fireConfetti();
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleStepChange = (step: number) => {
    if (isDev) {
      setActiveStep(step);
      // Generate fake data for skipped steps
      if (step > 0 && !companyData) {
        setCompanyData({ id: 999, nom: 'Dev Company', limitUser: 100 });
      }
      if (step > 1 && invitedUsers.length === 0) {
        setInvitedUsers([
          { id: '1', email: 'user1@dev.com', firstName: 'John', lastName: 'Doe' },
          { id: '2', email: 'user2@dev.com', firstName: 'Jane', lastName: 'Smith' }
        ]);
      }
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompanyData(null);
    setInvitedUsers([]);
    setShowWelcome(true);
    sessionStorage.removeItem('onboarding_start_step');
  };

  const handleClose = async () => {
    // Mark user as onboarded
    try {
      await axiosInstance.patch('/users/me/onboarding', {
        hasBeenOnboarded: true
      });
      // Clear onboarding state from session
      sessionStorage.removeItem('onboardingState');
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }

    navigate('/');
    window.location.reload(); // Force reload to update the UI state
  };

  const handleCompanyCreated = (data: any) => {
    console.log('Company data received in B2BWalaxyStyle:', data);
    setCompanyData(data);
    unlockAchievement('company-created');
    handleNext();
  };

  const handleUsersInvited = (users: any[]) => {
    setInvitedUsers(users);
    if (users.length > 0) {
      unlockAchievement('team-invited');
    }
    handleNext();
  };

  const handleDemoCompleted = () => {
    unlockAchievement('features-discovered');
    handleNext();
  };

  const handleTrialStarted = async () => {
    unlockAchievement('trial-started');
    fireConfetti();

    // Mark user as onboarded
    try {
      await axiosInstance.patch('/users/me/onboarding', {
        hasBeenOnboarded: true
      });
      // Clear onboarding state from session
      sessionStorage.removeItem('onboardingState');
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }

    setTimeout(() => {
      navigate('/');
      window.location.reload(); // Force reload to update the UI state
    }, 3000);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <CompanySetupWalaxy onComplete={handleCompanyCreated} />;
      case 1:
        console.log('Rendering TeamInviteWalaxy with companyData:', companyData);
        return <TeamInviteWalaxy onComplete={handleUsersInvited} companyId={companyData?.id} />;
      case 2:
        return <FeatureDemoWalaxy onComplete={handleDemoCompleted} companyData={companyData} />;
      case 3:
        return <PaywallWalaxy onComplete={handleTrialStarted} companyData={companyData} invitedUsers={invitedUsers} />;
      default:
        return null;
    }
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  // Clean up session storage when component unmounts
  useEffect(() => {
    return () => {
      if (isDev) {
        sessionStorage.removeItem('onboarding_start_step');
      }
    };
  }, [isDev]);

  return (
    <>
      {/* Dev Tools */}
      {isDev && (
        <OnboardingDevTools
          activeStep={activeStep}
          totalSteps={steps.length}
          onStepChange={handleStepChange}
          onSkipWelcome={() => setShowWelcome(false)}
          onReset={handleReset}
          onClose={handleClose}
          showWelcome={showWelcome}
          steps={steps}
        />
      )}

      {/* Backdrop to ensure nothing shows through */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: theme.palette.mode === 'dark' ? 'black' : 'white',
          zIndex: 1299
        }}
      />

      {/* Welcome Overlay */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
            >
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Box sx={{ mx: 'auto', mb: 4 }}>
                  <Player
                    src="/welcome.json"
                    autoplay
                    loop
                    style={{ width: '400px', height: '400px' }}
                  />
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ mb: 2 }}>
                  {translate('onboarding.b2b.welcome.title')}
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                  {translate('onboarding.b2b.welcome.subtitle')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setShowWelcome(false)}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.9),
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {translate('onboarding.b2b.welcome.cta')}
                </Button>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Onboarding */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          background: theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : theme.palette.background.paper,
          backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : '#FAFAFA',
          zIndex: 1300
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: 100,
            display: 'flex',
            alignItems: 'center',
            px: 4,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: theme.palette.background.paper,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    fontSize: '2rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.1)
                  }}
                >
                  {steps[activeStep].emoji}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {steps[activeStep].title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {steps[activeStep].subtitle}
                  </Typography>
                </Box>
              </Box>

              {/* Progress Pills - Centered */}
              <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                <Stack direction="row" spacing={1}>
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={false}
                      animate={{
                        scale: index === activeStep ? 1.1 : 1,
                      }}
                    >
                      <Chip
                        label={`${index + 1}`}
                        size="small"
                        sx={{
                          bgcolor: index <= activeStep
                            ? theme.palette.primary.main
                            : alpha(theme.palette.action.disabled, 0.1),
                          color: index <= activeStep ? 'white' : 'text.disabled',
                          fontWeight: 600,
                          minWidth: 32,
                          height: 32,
                          '& .MuiChip-label': {
                            px: 0
                          }
                        }}
                      />
                    </motion.div>
                  ))}
                </Stack>
              </Box>

              <Tooltip title={translate('common.close')}>
                <IconButton onClick={() => navigate('/dashboard')}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mt: 2, position: 'relative' }}>
              <Box
                sx={{
                  height: 6,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  style={{
                    height: '100%',
                    background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    borderRadius: 3
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: -20,
                  fontWeight: 600,
                  color: theme.palette.primary.main
                }}
              >
                {Math.round(progress)}%
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 4 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              style={{ height: '100%' }}
            >
              <Container maxWidth="lg" sx={{ height: '100%' }}>
                {renderStep()}
              </Container>
            </motion.div>
          </AnimatePresence>
        </Box>

      </Box>
    </>
  );
};
